// ============================================
// Auth Service - Manejo de autenticación
// ============================================

export interface LoginRequest {
  Username: string;
  Password: string;
}

export interface LoginResponse {
  Success: boolean;
  Message: string;
  Token?: string;
  TokenExpiry?: string;
  User?: {
    UserId: number;
    Username: string;
    Email: string;
    FullName: string;
    Role: string;
    LastLoginAt?: string;
  };
}

export interface ApiResponse<T> {
  Success: boolean;
  Message: string;
  Data: T;
  Errors?: string[];
}

// Detectar automáticamente la URL del API según el entorno
const getApiUrl = (): string => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  const hostname = window.location.hostname;
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // Desarrollo local
    return 'http://localhost:5000/api';
  } else {
    // Producción - usar el mismo origen (el proxy reverso redirige al backend)
    return `${window.location.origin}/api`;
  }
};

const API_URL = getApiUrl();

class AuthService {
  private readonly TOKEN_KEY = 'admin_token';
  private readonly USER_KEY = 'admin_user';

  /**
   * Login del usuario admin
   */
  async login(username: string, password: string): Promise<LoginResponse> {
    try {
      const response = await fetch(`${API_URL}/Auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ Username: username, Password: password })
      });

      const data: LoginResponse = await response.json();

      if (data.Success && data.Token) {
        // Guardar token y usuario en localStorage
        localStorage.setItem(this.TOKEN_KEY, data.Token);
        if (data.User) {
          localStorage.setItem(this.USER_KEY, JSON.stringify(data.User));
        }
      }

      return data;
    } catch (error: any) {
      console.error('Error during login:', error);
      return {
        Success: false,
        Message: error.message || 'Error al comunicarse con el servidor'
      };
    }
  }

  /**
   * Solicitar restablecimiento de contraseña
   */
  async forgotPassword(email: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetch(`${API_URL}/Auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ Email: email })
      });

      const data: ApiResponse<any> = await response.json();

      return {
        success: data.Success,
        message: data.Message
      };
    } catch (error: any) {
      console.error('Error during forgot password:', error);
      return {
        success: false,
        message: error.message || 'Error al comunicarse con el servidor'
      };
    }
  }

  /**
   * Restablecer contraseña con token
   */
  async resetPassword(token: string, newPassword: string, confirmPassword: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetch(`${API_URL}/Auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          Token: token,
          NewPassword: newPassword,
          ConfirmPassword: confirmPassword
        })
      });

      const data: ApiResponse<any> = await response.json();

      return {
        success: data.Success,
        message: data.Message
      };
    } catch (error: any) {
      console.error('Error during reset password:', error);
      return {
        success: false,
        message: error.message || 'Error al comunicarse con el servidor'
      };
    }
  }

  /**
   * Logout
   */
  logout(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
  }

  /**
   * Obtener token guardado
   */
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Obtener usuario guardado
   */
  getUser(): any | null {
    const userStr = localStorage.getItem(this.USER_KEY);
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch {
        return null;
      }
    }
    return null;
  }

  /**
   * Obtener usuario actual (alias de getUser)
   */
  getCurrentUser(): any | null {
    return this.getUser();
  }

  /**
   * Verificar si está autenticado
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;

    // Verificar si el token ha expirado (simple check basado en payload)
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000; // Convertir a milisegundos
      return Date.now() < exp;
    } catch {
      return false;
    }
  }

  /**
   * Obtener headers con autorización
   */
  getAuthHeaders(): HeadersInit {
    const token = this.getToken();
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    };
  }

  /**
   * Verificar si el sistema requiere setup inicial
   */
  async hasAdmin(): Promise<boolean> {
    try {
      const response = await fetch(`${API_URL}/Auth/has-admin`);
      const data: ApiResponse<boolean> = await response.json();
      return data.Data;
    } catch (error) {
      console.error('Error checking admin:', error);
      return true; // Asumir que ya está configurado en caso de error
    }
  }

  /**
   * Configurar primer administrador
   */
  async setupFirstAdmin(username: string, email: string, fullName: string, password: string, confirmPassword: string): Promise<LoginResponse> {
    try {
      const response = await fetch(`${API_URL}/Auth/setup-first-admin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          Username: username,
          Email: email,
          FullName: fullName,
          Password: password,
          ConfirmPassword: confirmPassword
        })
      });

      const data: LoginResponse = await response.json();

      if (data.Success && data.Token) {
        localStorage.setItem(this.TOKEN_KEY, data.Token);
        if (data.User) {
          localStorage.setItem(this.USER_KEY, JSON.stringify(data.User));
        }
      }

      return data;
    } catch (error: any) {
      console.error('Error during setup:', error);
      return {
        Success: false,
        Message: error.message || 'Error al comunicarse con el servidor'
      };
    }
  }
}

export const authService = new AuthService();